import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, User, LogOut, LayoutDashboard, Globe } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLocalePath, useLocalizedNavigate } from '@/hooks/useLocalePath';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, language } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const navigate = useLocalizedNavigate();
  const { lang, lp } = useLocalePath();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#products', label: t('nav.products') },
    { href: '#pricing', label: t('nav.pricing') },
    { href: '#status', label: t('nav.status') },
  ];

  const pageLinks = [
    { to: lp('/blog'), label: t('nav.blog') },
    { to: lp('/about'), label: t('footer.about') },
    { to: lp('/contact'), label: t('footer.contact') },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const scrollToSection = (href: string) => {
    setIsOpen(false);
    const basePath = lp('/');
    if (!location.pathname.endsWith(`/${lang}`) && location.pathname !== basePath) {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href);
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    } else {
      const element = document.querySelector(href);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const switchLang = () => {
    const newLang = lang === 'ir' ? 'en' : 'ir';
    localStorage.setItem('preferred_lang', newLang);
    // Replace /ir/ or /en/ prefix in current path
    const newPath = location.pathname.replace(`/${lang}`, `/${newLang}`);
    window.location.href = newPath;
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'glass border-b border-border/30 py-2' : 'bg-transparent py-4'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to={lp('/')} className="text-2xl font-bold text-foreground font-['Orbitron'] tracking-wider hover:text-primary transition-colors flex items-center gap-2">
            <span className="text-primary">Ziqo</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
            {pageLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
            {user && (
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 relative group"
              >
                {t('dashboard.title')}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
              </button>
            )}
          </div>

          {/* Controls */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={switchLang}
              className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted/50 transition-all duration-300 flex items-center gap-1 text-xs font-bold"
              aria-label="Switch language"
            >
              <Globe className="w-4 h-4" />
              {lang === 'ir' ? 'EN' : 'فا'}
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted/50 transition-all duration-300"
              aria-label={language === 'fa' ? 'تغییر تم' : 'Toggle theme'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Auth Button */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-primary">
                    <User className="w-4 h-4" />
                    <span className="max-w-20 truncate text-sm">{user.user_metadata?.username || (language === 'fa' ? 'کاربر' : 'User')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-48 glass-card animate-fade-in"
                >
                  <DropdownMenuItem className="text-xs text-muted-foreground cursor-default text-right" dir={language === 'fa' ? 'rtl' : 'ltr'}>
                    {user.email}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard')} 
                    className="cursor-pointer hover:bg-primary/10 transition-colors justify-start"
                  >
                    <LayoutDashboard className="w-4 h-4 ml-2" />
                    {t('dashboard.title')}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleSignOut} 
                    className="text-destructive cursor-pointer hover:bg-destructive/10 transition-colors justify-start"
                  >
                    <LogOut className="w-4 h-4 ml-2" />
                    {t('dashboard.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                onClick={() => navigate('/auth')}
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium glow-sm"
              >
                {language === 'fa' ? 'ورود' : 'Login'}
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-foreground hover:text-primary transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={language === 'fa' ? 'باز کردن منو' : 'Open menu'}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-6 animate-fade-in-up">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-muted-foreground hover:text-primary transition-colors py-2 text-right"
                >
                  {link.label}
                </button>
              ))}
              {pageLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsOpen(false)}
                  className="text-muted-foreground hover:text-primary transition-colors py-2 text-right"
                >
                  {link.label}
                </Link>
              ))}
              
              <div className="flex items-center justify-between py-4 border-t border-border/30">
                <button
                  onClick={switchLang}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                >
                  <Globe className="w-4 h-4" />
                  {lang === 'ir' ? 'English' : 'فارسی'}
                </button>
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                >
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  {theme === 'dark' ? (language === 'fa' ? 'روشن' : 'Light') : (language === 'fa' ? 'تاریک' : 'Dark')}
                </button>
              </div>

              {user ? (
                <div className="space-y-3 pt-2">
                  <div className="text-xs text-muted-foreground text-right">
                    {user.email}
                  </div>
                  <Button 
                    onClick={() => {
                      navigate('/dashboard');
                      setIsOpen(false);
                    }}
                    variant="outline" 
                    size="sm"
                    className="w-full border-primary/30 justify-start"
                  >
                    <LayoutDashboard className="w-4 h-4 ml-2" />
                    {t('dashboard.title')}
                  </Button>
                  <Button 
                    onClick={handleSignOut}
                    variant="ghost" 
                    size="sm"
                    className="w-full text-destructive hover:bg-destructive/10 justify-start"
                  >
                    <LogOut className="w-4 h-4 ml-2" />
                    {t('dashboard.logout')}
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => {
                    navigate('/auth');
                    setIsOpen(false);
                  }}
                  className="w-full bg-primary text-primary-foreground font-medium"
                >
                  {language === 'fa' ? 'ورود / ثبت نام' : 'Login / Sign Up'}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
